import React from "react";
import { Avatar, StatusBadge, WeightBadge, TimelineBadge, TimelineStatusBadge } from "@/components/UI";
import { PRIORITY_META } from "@/lib/constants";
import { AlertTriangle, RefreshCw, MessageSquare, Link2 } from "lucide-react";

// Corporate accent colors per priority (left border stripe)
const NEON = {
  low: { accent: "#94A3B8" },
  medium: { accent: "#64748B" },
  high: { accent: "#F59E0B" },
  urgent: { accent: "#FBBF24" },
};

export default function TaskCard({ task, onClick, showStatus = false }) {
  const overdue = task.is_overdue;
  const n = NEON[task.priority] || NEON.medium;

  return (
    <div
      onClick={() => onClick?.(task)}
      data-testid={`task-card-${task.id}`}
      title={`Priority: ${PRIORITY_META[task.priority]?.label || "Medium"}`}
      style={{ borderLeftColor: n.accent }}
      className="group relative mt-3 cursor-pointer rounded-md border border-white/10 border-l-[3px] bg-[#0F172A] p-3.5 shadow-sm shadow-black/30 transition-colors duration-150 hover:border-white/20"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="font-heading text-sm font-semibold leading-snug text-slate-100 group-hover:text-white line-clamp-2">
          {task.title}
        </p>
        <div className="flex shrink-0 gap-1">
          {task.review_url && (
            <span className="text-slate-400" title="Ada URL review">
              <Link2 size={14} />
            </span>
          )}
          {task.comments_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-slate-400" title={`${task.comments_count} komentar`}>
              <MessageSquare size={14} /> {task.comments_count}
            </span>
          )}
          {task.is_urgent && (
            <AlertTriangle size={14} className="text-slate-400" data-testid={`task-urgent-icon-${task.id}`} />
          )}
          {task.status === "replaced" && <RefreshCw size={14} className="text-slate-400" />}
        </div>
      </div>

      {task.project && (
        <div className="mb-2 flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: task.project.color }} />
          <span className="text-xs text-slate-400">{task.project.name}</span>
        </div>
      )}

      <div className="mb-2.5 flex flex-wrap gap-1.5">
        <WeightBadge weight={task.weight} />
        <TimelineBadge
          date={task.deadline}
          className={overdue ? "!border-rose-500/50 !bg-rose-500/10 !text-rose-400" : ""}
        />
      </div>

      {task.status === "review" && task.submitted_at && (
        <p className="mb-2 text-[11px] text-slate-400" data-testid={`task-submitted-${task.id}`}>
          Submit {new Date(task.submitted_at).toLocaleDateString("id-ID", { day: "numeric", month: "short" })}
          {" • menunggu "}
          {Math.max(0, Math.floor((Date.now() - new Date(task.submitted_at).getTime()) / 86400000))} hari
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Avatar src={task.assignee?.avatar_url} name={task.assignee?.name || "?"} size={20} />
          <span className="text-xs text-slate-400">{task.assignee?.name || "Unassigned"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <TimelineStatusBadge timelineStatus={task.timeline_status} />
          {showStatus && <StatusBadge status={task.status} />}
        </div>
      </div>
    </div>
  );
}
